import { useActorRef, useSelector } from '@xstate/react';
import { useCallback, useEffect, useRef } from 'react';
import type { FeeAuthorization } from '~/features/employer/billing/types';
import { createOpportunitiesMachine } from './createOpportunitiesMachine';
import { useDeepLinkedResponse } from './deepLinkParams';

export const useOpportunitiesMachineService = <
  TOpportunity extends { responseId: number },
  TMessage,
  TFilter extends string,
>(
  machine: ReturnType<typeof createOpportunitiesMachine<TOpportunity, TMessage, TFilter>>
) => {
  // [MCV-1549] Both surfaces built on this machine — the provider's job
  // opportunities and the B2B sent view — are notification destinations, so the
  // deep link is handled here rather than in each of them.
  const { responseId: deepLinkedResponseId, navigationKey } = useDeepLinkedResponse();

  // Machine input covers the first mount. It cannot cover the second click:
  // notification rows are <Link>s, so arriving from another notification for
  // this same route swaps the query string without remounting, and the actor
  // (with its input) survives.
  //
  // Latched on the navigation rather than the id. Several notifications about
  // one response are normal — a message, then a status change — and if the user
  // has clicked another row in between, the second one has to move them back.
  // An id-keyed latch cannot tell "same id as last time" from "already handled"
  // and swallows every notification after the first for that response. Seeding
  // with the mount key is what stops the arrival id being re-sent as a
  // redundant event below.
  const lastNavigationRef = useRef<string | undefined>(navigationKey);

  const machineRef = useActorRef(machine, {
    input: { initialResponseId: deepLinkedResponseId },
  });

  const opportunities = useSelector(machineRef, (s) => s.context.opportunities);
  const totalCount = useSelector(machineRef, (s) => s.context.totalCount);
  const selectedOpportunity = useSelector(machineRef, (s) => s.context.selectedOpportunity);
  const messages = useSelector(machineRef, (s) => s.context.messages);
  const filter = useSelector(machineRef, (s) => s.context.filter);
  const includeDeclined = useSelector(machineRef, (s) => s.context.includeDeclined);
  const loading = useSelector(machineRef, (s) => s.context.loading);
  const messagesLoading = useSelector(machineRef, (s) => s.context.messagesLoading);
  const sendingMessage = useSelector(machineRef, (s) => s.matches('$_SEND_MESSAGE'));
  const writing = useSelector(
    machineRef,
    (s) => s.matches('$_SEND_MESSAGE') || s.matches('$_UPDATE_STATUS')
  );
  const uncertainAction = useSelector(machineRef, (s) => s.context.uncertainAction);
  const sendReceipt = useSelector(machineRef, (s) => s.context.sendReceipt);
  const retainedReply = useSelector(machineRef, (s) =>
    s.context.selectedOpportunity
      ? s.context.retainedReplies[s.context.selectedOpportunity.responseId]
      : undefined
  );
  const canAcknowledgeUncertainty = useSelector(
    machineRef,
    (s) =>
      s.matches('$_VIEW') &&
      s.context.uncertaintyReviewed &&
      s.context.uncertainAction?.responseId === s.context.selectedOpportunity?.responseId
  );
  const error = useSelector(machineRef, (s) => s.context.error);
  const feeRefusal = useSelector(machineRef, (s) => s.context.feeRefusal);

  useEffect(() => {
    machineRef.send({ type: 'ON_LOAD' });
  }, [machineRef]);

  useEffect(() => {
    if (deepLinkedResponseId == null) return;
    if (navigationKey === lastNavigationRef.current) return;
    lastNavigationRef.current = navigationKey;
    machineRef.send({ type: 'ON_DEEP_LINK', responseId: deepLinkedResponseId });
  }, [deepLinkedResponseId, navigationKey, machineRef]);

  const handleSelect = useCallback(
    (responseId: number) => machineRef.send({ type: 'ON_SELECT', responseId }),
    [machineRef]
  );

  const handleFilter = useCallback(
    (newFilter: TFilter) => machineRef.send({ type: 'ON_FILTER', filter: newFilter }),
    [machineRef]
  );

  const handleIncludeDeclined = useCallback(
    (next: boolean) => machineRef.send({ type: 'ON_INCLUDE_DECLINED', includeDeclined: next }),
    [machineRef]
  );

  const handleSendMessage = useCallback(
    (messageBody: string) => machineRef.send({ type: 'ON_SEND_MESSAGE', messageBody }),
    [machineRef]
  );

  const handleAccept = useCallback(() => machineRef.send({ type: 'ON_ACCEPT' }), [machineRef]);

  // [MCV-1889] A responder who pays accepts with the fee schedule they just reviewed.
  const handleAcceptWithFee = useCallback(
    (feeAuthorization?: FeeAuthorization) =>
      machineRef.send({
        type: 'ON_ACCEPT',
        ...(feeAuthorization ? { feeAuthorization } : {}),
      }),
    [machineRef]
  );

  const handleWithdraw = useCallback(() => machineRef.send({ type: 'ON_WITHDRAW' }), [machineRef]);

  const handleReload = useCallback(() => machineRef.send({ type: 'ON_LOAD' }), [machineRef]);

  const consumeFeeRefusal = useCallback(
    () => machineRef.send({ type: 'ON_CONSUME_FEE_REFUSAL' }),
    [machineRef]
  );

  return {
    opportunities,
    totalCount,
    selectedOpportunity,
    messages,
    filter,
    includeDeclined,
    loading,
    messagesLoading,
    sendingMessage,
    writing,
    uncertainAction,
    sendReceipt,
    retainedReply,
    handleEditReply: (messageBody: string) =>
      machineRef.send({ type: 'ON_EDIT_REPLY', messageBody }),
    canAcknowledgeUncertainty,
    handleReviewUncertainty: () => {
      if (uncertainAction)
        machineRef.send({ type: 'ON_DEEP_LINK', responseId: uncertainAction.responseId });
    },
    handleAcknowledgeUncertainty: () => machineRef.send({ type: 'ON_ACKNOWLEDGE_UNCERTAINTY' }),
    handleDiscardUncertainty: () => machineRef.send({ type: 'ON_DISCARD_UNCERTAINTY' }),
    error,
    feeRefusal,
    consumeFeeRefusal,
    replyError: writing ? null : (retainedReply?.error ?? null),
    handleSelect,
    handleFilter,
    handleIncludeDeclined,
    handleSendMessage,
    handleAccept,
    handleAcceptWithFee,
    handleWithdraw,
    handleReload,
  };
};
